import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageContext'
import { getCanonicalServiceKey, getLocalizedServiceSlug, getServiceUrl } from '../i18n/routes'
import MetaTags from '../components/MetaTags'
import Lightbox from '../components/Lightbox'

export default function ServiceDetailPage() {
  const { slug } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { lang, t, getPageUrl } = useLanguage()
  const [galleryImages, setGalleryImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  const canonicalKey = getCanonicalServiceKey(slug)
  const expectedSlug = getLocalizedServiceSlug(canonicalKey, lang)

  // Redirect if visiting with incorrect language slug variant (e.g. /transport/specialized-transport -> /transport/specialtransport)
  useEffect(() => {
    if (slug && slug.toLowerCase() !== expectedSlug) {
      const correctPath = getServiceUrl(canonicalKey, lang)
      if (location.pathname !== correctPath) {
        navigate(correctPath, { replace: true })
      }
    }
  }, [slug, expectedSlug, canonicalKey, lang, location.pathname, navigate])

  const serviceData = t.servicesList[canonicalKey] || t.servicesList.specialtransport

  const heroImagesMap = {
    specialtransport: 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg',
    containerkoersel: 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg',
    stykgods: 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476816859-656735350025511-354367063843939379-n.jpg',
    stoebegods: 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476816859-656735350025511-354367063843939379-n.jpg',
    traepiller: 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470183366-1004865451686234-2145888141808669137-n.jpg',
    'national-transport': 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476448836-655303320168714-1260139362731958487-n.jpg',
    'international-transport': 'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470160342-1004853748354071-1772744319645786737-n.jpg',
  }

  const defaultGalleriesMap = {
    specialtransport: [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/734101346-1054615956904113-4096849147404615392-n.jpg'
    ],
    containerkoersel: [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470094745-1004853755020737-7287025073737737011-n.jpg'
    ],
    stykgods: [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476816859-656735350025511-354367063843939379-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/206196946-165242755648512-6798365573135249439-n.jpg'
    ],
    stoebegods: [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476816859-656735350025511-354367063843939379-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/206196946-165242755648512-6798365573135249439-n.jpg'
    ],
    traepiller: [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470183366-1004865451686234-2145888141808669137-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476448836-655303320168714-1260139362731958487-n.jpg'
    ],
    'national-transport': [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476448836-655303320168714-1260139362731958487-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/734101346-1054615956904113-4096849147404615392-n.jpg'
    ],
    'international-transport': [
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470160342-1004853748354071-1772744319645786737-n.jpg',
      'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg'
    ]
  }

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/galleries')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const matched = data.find((g) => g.gallery_slug === canonicalKey || (canonicalKey === 'stykgods' && g.gallery_slug === 'stoebegods'))
          if (matched && Array.isArray(matched.images) && matched.images.length > 0) {
            setGalleryImages(matched.images)
            return
          }
        }
        setGalleryImages(defaultGalleriesMap[canonicalKey] || defaultGalleriesMap.stykgods || [])
      })
      .catch(() => {
        setGalleryImages(defaultGalleriesMap[canonicalKey] || defaultGalleriesMap.stykgods || [])
      })
  }, [canonicalKey])

  const heroImage = heroImagesMap[canonicalKey] || heroImagesMap.specialtransport

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <MetaTags
        title={serviceData.metaTitle}
        description={serviceData.metaDesc}
      />

      {/* Hero Header */}
      <section className="bg-gradient-to-br from-slate-100 via-sky-50/60 to-white text-slate-900 py-12 lg:py-16 relative overflow-hidden border-b border-slate-200">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Link
              to={getPageUrl('services')}
              className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-bold text-xs uppercase tracking-wider mb-4 transition-colors"
            >
              <i className="fa-solid fa-arrow-left"></i>
              <span>{t.serviceDetail.backToServices}</span>
            </Link>

            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
              {serviceData.title}
            </h1>

            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              {serviceData.shortDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content & Features */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Main Description */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">
              {serviceData.title}
            </h2>

            <div 
              className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-base space-y-4"
              dangerouslySetInnerHTML={{ __html: serviceData.description }}
            />

            {serviceData.features && (
              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">{t.serviceDetail.keyBenefits}</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceData.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 bg-white p-3 border border-slate-200 rounded-none shadow-xs">
                      <i className="fa-solid fa-check text-sky-600 shrink-0 mt-0.5"></i>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Gallery for this service if available */}
            {galleryImages && galleryImages.length > 0 && (
              <div className="pt-8 border-t border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">{t.serviceDetail.galleryTitle}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setLightboxIndex(idx)
                        setIsLightboxOpen(true)
                      }}
                      className="aspect-square bg-slate-100 rounded-none overflow-hidden group border border-slate-200 cursor-pointer"
                    >
                      <img 
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`} 
                        alt={`${serviceData.title} ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar CTA */}
          <div className="space-y-6">
            <div className="rounded-none overflow-hidden shadow-sm border border-slate-200 bg-white">
              <img 
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`} 
                alt={serviceData.title}
                className="w-full h-56 object-cover"
              />
              <div className="p-6 bg-slate-900 text-white space-y-4">
                <h3 className="text-xl font-bold">
                  {t.serviceDetail.ctaBoxTitle} {serviceData.title.toLowerCase()}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {t.serviceDetail.ctaBoxDesc}
                </p>

                <div className="pt-2 space-y-3">
                  <a
                    href="tel:+4525303016"
                    className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 px-4 rounded-none flex items-center justify-center gap-2 transition-colors text-sm shadow-md"
                  >
                    <i className="fa-solid fa-phone text-sm"></i>
                    <span>+45 25 30 30 16</span>
                  </a>

                  <Link
                    to={getPageUrl('contact')}
                    className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold py-3 px-4 rounded-none flex items-center justify-center gap-2 transition-colors text-sm"
                  >
                    <i className="fa-solid fa-envelope text-sm"></i>
                    <span>{t.serviceDetail.sendInquiry}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={galleryImages}
        currentIndex={lightboxIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />
    </div>
  )
}
